import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * @typedef {Object} CartMutationQueueEntry
 * @property {() => Promise<unknown>} task
 * @property {string | undefined} coalesceKey
 * @property {(value: unknown) => void} resolve
 * @property {(reason?: unknown) => void} reject
 * @property {Promise<unknown>} promise
 */

/**
 * @typedef {Object} CartMutationController
 * @property {<T>(task: () => Promise<T>, options?: { coalesceKey?: string }) => Promise<T>} enqueue
 */

const CART_MUTATION_CONTROLLER_KEY = 'PPCartMutations';
const CART_CONFLICT_RETRY_DELAY = 250;

/**
 * Returns the shared FIFO cart queue. When a coalesce key is supplied, a queued (not running) task
 * with the same key is replaced, and every caller resolves or rejects with the replacement task.
 *
 * @returns {CartMutationController}
 */
function getCartMutationController() {
  const scope = /** @type {Window & Record<string, unknown>} */ (window);
  const existingController = /** @type {CartMutationController | undefined} */ (
    scope[CART_MUTATION_CONTROLLER_KEY]
  );

  if (typeof existingController?.enqueue === 'function') return existingController;

  /** @type {CartMutationQueueEntry[]} */
  const queue = [];
  /** @type {Map<string, CartMutationQueueEntry>} */
  const queuedByKey = new Map();
  let isRunning = false;

  const drain = async () => {
    if (isRunning) return;
    isRunning = true;

    while (queue.length > 0) {
      const entry = queue.shift();
      if (!entry) continue;

      if (entry.coalesceKey && queuedByKey.get(entry.coalesceKey) === entry) {
        queuedByKey.delete(entry.coalesceKey);
      }

      try {
        entry.resolve(await entry.task());
      } catch (error) {
        entry.reject(error);
      }
    }

    isRunning = false;
  };

  const controller = /** @type {CartMutationController} */ ({
    enqueue(task, options = {}) {
      const { coalesceKey } = options;
      const queuedEntry = coalesceKey ? queuedByKey.get(coalesceKey) : undefined;

      if (queuedEntry) {
        queuedEntry.task = task;
        return /** @type {Promise<ReturnType<typeof task>>} */ (queuedEntry.promise);
      }

      /** @type {(value: unknown) => void} */
      let resolve = () => {};
      /** @type {(reason?: unknown) => void} */
      let reject = () => {};
      const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      const entry = { task, coalesceKey, resolve, reject, promise };

      queue.push(entry);
      if (coalesceKey) queuedByKey.set(coalesceKey, entry);
      void drain();

      return /** @type {Promise<ReturnType<typeof task>>} */ (promise);
    },
  });

  scope[CART_MUTATION_CONTROLLER_KEY] = controller;
  return controller;
}

/**
 * @param {string} url
 * @param {RequestInit} config
 * @returns {Promise<Record<string, any>>}
 */
async function fetchCartJSON(url, config) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await fetch(url, config);
    let data;

    try {
      data = await response.json();
    } catch {
      throw new Error('The cart returned an invalid response. Please try again.');
    }

    if (response.status === 409 && attempt === 0) {
      await new Promise((resolve) => window.setTimeout(resolve, CART_CONFLICT_RETRY_DELAY));
      continue;
    }

    if (!response.ok || data.errors || data.status >= 400) {
      const error = new Error(getCartErrorMessage(data));
      Object.assign(error, { status: response.status || data.status || 0 });
      throw error;
    }

    return data;
  }

  throw Object.assign(new Error('The cart is busy. Please try again.'), { status: 409 });
}

/**
 * @param {Record<string, any>} data
 * @returns {string}
 */
function getCartErrorMessage(data) {
  if (typeof data.errors === 'string') return data.errors;
  if (typeof data.message === 'string') return data.message;
  if (data.errors && typeof data.errors === 'object') return Object.values(data.errors).flat().join(' ');
  return "We couldn't update your discount. Please try again.";
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getReadableMutationError(error) {
  if (error instanceof TypeError) return "We couldn't connect to the store. Check your connection and try again.";
  return error instanceof Error && error.message ? error.message : "We couldn't update your discount. Please try again.";
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 *
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {(() => Promise<void>) | null} */
  #retryAction = null;

  /**
   * Handles applying a discount code.
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const code = discountCode.value.trim();
    if (!code) {
      this.#handleDiscountError('discount_code');
      return;
    }

    const action = () => this.#applyCode(code, discountCode);
    await this.#runMutation(action, event, 'apply');
  };

  /**
   * Handles removing a discount code.
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!(event.target instanceof HTMLElement) || typeof this.dataset.sectionId !== 'string') return;

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const code = pill.dataset.discountCode;
    if (!code) return;

    const action = () => this.#removeCode(code);
    await this.#runMutation(action, event, 'remove');
  };

  /**
   * @param {string} code
   * @param {HTMLInputElement} input
   */
  async #applyCode(code, input) {
    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(code)) return;

    this.#hideErrors();

    const body = JSON.stringify({
      discount: [...existingDiscounts, code].join(','),
      sections: [this.dataset.sectionId],
    });
    const data = await fetchCartJSON(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const discountCodes = Array.isArray(data.discount_codes) ? data.discount_codes : [];

    if (discountCodes.some((discount) => discount.code === code && discount.applicable === false)) {
      input.value = '';
      this.#handleDiscountError('discount_code');
      return;
    }

    const newHtml = data.sections?.[this.dataset.sectionId];
    if (typeof newHtml !== 'string') throw new Error("We couldn't refresh your cart. Please try again.");

    const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
    const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const renderedCodes = Array.from(section?.querySelectorAll('.cart-discount__pill') || [])
      .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
      .filter(Boolean);

    const isShippingOnlyCode =
      renderedCodes.length === existingDiscounts.length &&
      renderedCodes.every((renderedCode) => existingDiscounts.includes(renderedCode)) &&
      discountCodes.some((discount) => discount.code === code && discount.applicable === true);

    if (isShippingOnlyCode) {
      input.value = '';
      this.#handleDiscountError('shipping');
      return;
    }

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    await this.#morphOrRefreshSection(newHtml);
  }

  /**
   * @param {string} code
   */
  async #removeCode(code) {
    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(code);
    if (index === -1) return;
    existingDiscounts.splice(index, 1);

    this.#hideErrors();
    const body = JSON.stringify({
      discount: existingDiscounts.join(','),
      sections: [this.dataset.sectionId],
    });
    const data = await fetchCartJSON(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const newHtml = data.sections?.[this.dataset.sectionId];
    if (typeof newHtml !== 'string') throw new Error("We couldn't refresh your cart. Please try again.");

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    await this.#morphOrRefreshSection(newHtml);
  }

  /**
   * @param {() => Promise<void>} action
   * @param {Event} event
   * @param {'apply' | 'remove'} type
   */
  async #runMutation(action, event, type) {
    this.#setBusy(true);
    this.#retryAction = null;

    try {
      await getCartMutationController().enqueue(action);
    } catch (error) {
      console.error(error);
      const message = getReadableMutationError(error);
      this.#retryAction = action;
      this.#showRetryableError(message);
      document.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      this.#setBusy(false);
      if (type === 'apply') cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  /**
   * Handles a retry button click without requiring another form submission.
   */
  #retryLastMutation = () => {
    const action = this.#retryAction;
    if (!action) return;
    void this.#runMutation(action, new Event('retry'), 'remove');
  };

  /**
   * @param {'discount_code' | 'shipping'} type
   */
  #handleDiscountError(type) {
    this.#hideErrors();
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #hideErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    this.querySelector('[data-cart-discount-retry]')?.remove();
  }

  /**
   * @param {string} message
   */
  #showRetryableError(message) {
    this.#hideErrors();
    const { cartDiscountError } = this.refs;
    cartDiscountError.classList.remove('hidden');

    const wrapper = document.createElement('span');
    wrapper.dataset.cartDiscountRetry = '';

    const text = document.createElement('small');
    text.className = 'cart-discount__error-text cart-primary-typography';
    text.textContent = message;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--tertiary';
    button.textContent = 'Try again';
    button.addEventListener('click', this.#retryLastMutation);

    wrapper.append(text, button);
    cartDiscountError.append(wrapper);
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('button, input').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;

      if (busy) {
        control.dataset.cartWasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.dataset.cartWasDisabled !== undefined) {
        control.disabled = control.dataset.cartWasDisabled === 'true';
        delete control.dataset.cartWasDisabled;
      }
    });
  }

  /**
   * @param {string} sectionHtml
   */
  async #morphOrRefreshSection(sectionHtml) {
    try {
      await morphSection(this.dataset.sectionId, sectionHtml);
    } catch (error) {
      console.error(error);
      try {
        await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false });
      } catch (refreshError) {
        // The discount mutation succeeded, so do not offer a retry that could misrepresent state.
        console.error(refreshError);
      }
    }
  }

  /**
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    this.querySelectorAll('.cart-discount__pill').forEach((pill) => {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    });
    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
